import React, { useEffect } from 'react';
import LoginForm from '../components/LoginForm';
import Logo from '../../shared/components/Logo';
import { IconClock, IconShieldCheck, IconBuildingEstate } from '@tabler/icons-react';
import { useSkinData } from '../../services/common/hooks/useSkinData';
import FullPageSpinner from '../../shared/components/spinner/FullPageSpinner';
import FullPageError from '../../shared/components/errors/FullPageError';
import useFavicon from '../../shared/hooks/useFavicon';
import useDynamicManifest from '../../shared/hooks/useDynamicManifest';

const StatItem: React.FC<{ icon: React.ReactNode; value: string; label: string }> = ({ icon, value, label }) => (
    <div className="d-flex align-items-center">
        <div className="me-3">{icon}</div>
        <div>
            <div className="fs-4 fw-bold">{value}</div>
            <div className="text-white-50">{label}</div>
        </div>
    </div>
);

const baseUrl = window.location.origin; // Get the base URL from the current window location
const base64Url = btoa(baseUrl); // Encode the base URL in base64 format

const LoginPageCorporateV2: React.FC = () => {
    const { data, isLoading, isError } = useSkinData(base64Url);

    useFavicon(data?.data?.favicon_url || null);

    // Ambil warna dari skinData, jika tidak ada, gunakan warna default
    const themeColor = data?.data?.color || '#076a4e';
    const bgLeftPanelColor = data?.data?.additional?.login_bg_left || '#1a202c'; // Warna abu-abu gelap (slate)

    // PWA "Add to Home Screen" ikut branding whitelabel (bukan default statis "Djitu")
    useDynamicManifest({
        name: data?.data?.name,
        logoUrl: data?.data?.logo_url,
        themeColor,
    });

    useEffect(() => {
        // Pilih elemen root dari dokumen (<html>)
        const root = document.documentElement;

        // Setel nilai variabel CSS --tblr-primary secara dinamis
        root.style.setProperty('--tblr-primary', themeColor);

        document.title = data?.data?.name || 'Admin Panel'; // Set judul halaman

        // Cleanup function (opsional, tapi praktik yang baik)
        // Ini akan mengembalikan warna ke default jika komponen di-unmount
        return () => {
            root.style.removeProperty('--tblr-primary');

            document.title = 'Admin Panel'; // Reset title to default on unmount
        };
    }, [themeColor]);

    if (isLoading) {
        return <FullPageSpinner />;
    }

    if (isError) {
        return <FullPageError message="Failed to load skin data. Please try again later." />;
    }

    return (
        <div className="container-fluid vh-100 p-0">
            <div className="row g-0 h-100">
                {/* Panel Kiri: Branding & Kepercayaan */}
                <div
                    className="col-lg-6 d-none d-lg-flex flex-column justify-content-center p-5"
                    style={{ background: bgLeftPanelColor }} // Warna abu-abu gelap (slate)
                >
                    <div style={{ maxWidth: '500px', margin: '0 auto' }}>
                        <Logo src={data?.data?.logo_url} height="50" className="mb-5" />
                        <h1 className="display-5 fw-bolder text-white mb-4" style={{ fontSize: '2.8rem' }}>
                            {data?.data?.additional?.login_tagline || 'The Business Intelligence Platform for Hospitality Growth.'}
                        </h1>
                        <p className="lead text-white-50 mb-5">
                            {data?.data?.additional?.login_description || 'Make smarter, faster decisions with centralized data to optimize operations and maximize revenue.'}
                        </p>
                        <div className="d-flex text-white gap-5">
                            <StatItem icon={<IconBuildingEstate size={28} />} value={data?.data?.additional?.active_properties || '300+'} label="Active Properties" />
                            <StatItem icon={<IconShieldCheck size={28} />} value={data?.data?.additional?.uptime || '99.9%'} label="Uptime" />
                            <StatItem icon={<IconClock size={28} />} value={data?.data?.additional?.support || '24/7'} label="Support" />
                        </div>
                    </div>
                </div>

                {/* Panel Kanan: Form Login */}
                <div className="col-12 col-lg-6 d-flex justify-content-center align-items-center bg-white">
                    <div className="p-4" style={{ width: "100%", maxWidth: "420px" }}>
                        <div className="text-center mb-4 d-block d-lg-none">
                            <Logo src={data?.data?.logo_url} height="50" />
                        </div>
                        <div className="mb-5">
                            <h2 className="fs-1 fw-bolder mb-1">Sign In</h2>
                            <p className="text-muted">
                                Access your account to continue.
                            </p>
                        </div>
                        <LoginForm />
                        <div className="text-center mt-4">
                            <p className="text-muted">Don't have an account? <a href="#">Sign up now</a></p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginPageCorporateV2;